import type { RunSummary } from "../../types/outreach-program.types"
import { DEFERRED_LABELS, digestRunSummary, formatCentral } from "../../lib/program"

/** What one program run did: booked / deferred by cap / skipped by reason / errors. */
export function RunSummaryView({ summary, what }: { summary: RunSummary; what: string }) {
  const d = digestRunSummary(summary, what)
  const deferred = Object.entries(summary.deferred ?? {}).filter(([, n]) => (n ?? 0) > 0)
  const skipped = Object.entries(summary.skipped ?? {}).filter(([, n]) => n > 0)
  const errors = summary.errors ?? []
  return (
    <div className="rounded-lg border bg-muted/20 p-3 text-xs space-y-2" data-testid="run-summary">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium text-foreground">
          {what}
          {d.manual && (
            <span className="ml-1.5 rounded bg-warning/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-warning">
              manual run
            </span>
          )}
          {!d.manual && summary.mode && (
            <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
              {summary.mode}
            </span>
          )}
        </span>
        <span className="text-muted-foreground">{formatCentral(summary.finished_at ?? summary.started_at)}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Booked" value={d.booked} />
        <Stat label="Planned" value={d.planned} />
        <Stat label="Deferred" value={d.deferred} tone={d.deferred ? "warning" : undefined} />
        <Stat label="Skipped" value={d.skipped} tone={d.skipped ? "warning" : undefined} />
      </div>
      {deferred.length > 0 && (
        <p className="text-muted-foreground">
          Deferred: {deferred.map(([k, n]) => `${DEFERRED_LABELS[k] ?? k} ${n}`).join(" · ")}
        </p>
      )}
      {skipped.length > 0 && (
        <ul className="text-muted-foreground" aria-label="skipped by reason">
          {skipped.map(([reason, n]) => (
            <li key={reason}>
              <span className="tabular-nums font-medium text-foreground">{n}</span> {reason.replace(/_/g, " ")}
            </li>
          ))}
        </ul>
      )}
      {errors.length > 0 && (
        <p className="text-destructive">
          {errors.length} error{errors.length === 1 ? "" : "s"}:{" "}
          {errors
            .slice(0, 3)
            .map((e) => (typeof e === "string" ? e : String((e as any).error ?? (e as any).message ?? JSON.stringify(e))))
            .join("; ")}
        </p>
      )}
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "warning" }) {
  return (
    <div className="rounded-md bg-card px-2 py-1.5">
      <div className={tone === "warning" ? "text-base font-semibold tabular-nums text-warning" : "text-base font-semibold tabular-nums"}>
        {value}
      </div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  )
}
